import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from './AddButton';
import CancelButton from './CancelButton';
import Help from './Help';
import MenuSelector from './MenuSelector';
import PrimaryButton from './PrimaryButton';
import { ProjectConsumer } from './providers/ProjectProvider';
import { globalStyles as styles } from './style/gstheme';
import TitledPanel from './TitledPanel';
import TitledTextField from './TitledTextField';
import { DATATYPE_LIMITS, DATATYPES, MAX_LITERAL_LENGTH } from './utils/constants';
import { checkValidity, getDatatypeMenuOptions, sanitiseText } from './utils/utils';


class TypedLiteralDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            literal: "",
            error: false,
            existingValues: [],
            existing: false,
            datatype: DATATYPES.STRING,
            // We don't use boolean, datetime or langstring fields so exclude from list
            menuOptions: getDatatypeMenuOptions(["BOOLEAN", "DATETIME", "LANGSTRING", ...props.excludeDatatypes])
        };
    }

    // Need to check if an incoming revision causes a duplicate
    static getDerivedStateFromProps(props, state) {
        if (state.literal && state.literal !== "" && props.existingValues && props.existingValues.length > 0) {
            if (props.existingValues.length === state.existingValues.length) {
                const hasChanged = props.existingValues.some(ev => {
                    return !state.existingValues.some(sv => sv.text === ev.text && ev.datatype === sv.datatype);
                });
                if (!hasChanged) return null;
            }
            return { needsValidating: true, existingValues: props.existingValues };
        }
        return null;
    }

    componentDidUpdate() {
        if (this.state.needsValidating && this.state.literal && this.state.literal !== "") this.checkValidity();
    }

    handleClickOpen = () => { this.setState({ open: true }); };

    addLiteral = () => {
        this.setState({ open: false });
        this.props.onAddLiteral(this.state.literal, this.state.datatype);
        this.setState({ literal: "", datatype: DATATYPES.STRING });
    };

    exitAddLiteral = () => this.setState({
        open: false,
        literal: "",
        datatype: DATATYPES.STRING,
        error: false,
        existing: false
    });

    handleDatatypeChange = (datatype) => this.setState({ datatype }, this.checkValidity);

    checkValidity() {
        const { existingValues, maxLength } = this.props;
        const { datatype, literal, originalText } = this.state;

        if (literal.trim() === "" || this.state.literal.trim() === originalText) {
            this.setState({ error: false, existing: false });
            return;
        }
        let validity = checkValidity(literal.trim(), datatype, existingValues, maxLength);
        this.setState({ error: !validity.isValid, existing: validity.existing, needsValidating: false });
    }

    handleChange = (event) => {
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText }, this.checkValidity);
    };

    render() {

        const { literal, error, datatype, existing, menuOptions } = this.state;
        const { classes, textRef, title, name, placeholder, dataTest } = this.props;
        const datatypeLimits = DATATYPE_LIMITS[datatype];

        return (
            <ProjectConsumer>
                {({ projectDatatype }) => (
                    <React.Fragment>
                        <AddButton dataTest={dataTest} title="Add a new value" onClick={this.handleClickOpen} />
                        <Dialog
                            data-test="typed-literal"
                            maxWidth="md"
                            fullWidth
                            classes={{
                                paper: classes.paper,
                                paperWidthMd: classes.mediumDialog
                            }}
                            className={classes.dialog}
                            open={this.state.open}
                            onClose={this.exitAddLiteral}
                            aria-labelledby="createliteral-dialog-title">
                            <DialogTitle disableTypography className={classes.mainHeading} id="createliteral-dialog-title">{title}</DialogTitle>
                            <DialogContent style={{ height: '270px' }}>
                                {textRef && <Help textRef={textRef} />}
                                <form autoComplete="off" noValidate="novalidate">
                                    <TitledTextField
                                        dataTest="typed-literal-input"
                                        name="literal"
                                        title={name || "Text"}
                                        autoFocus={true}
                                        required={true}
                                        placeholder={placeholder}
                                        error={error || existing}
                                        text={literal}
                                        errorText={
                                            existing ?
                                                "This value already exists."
                                                :
                                                (error && datatype !== "" ?
                                                    "The value is not valid for the datatype."
                                                    :
                                                    null
                                                )
                                        }
                                        maxLength={datatypeLimits && datatypeLimits.maxLength ? datatypeLimits.maxLength : MAX_LITERAL_LENGTH}
                                        onEnter={this.addLiteral}
                                        onChange={this.handleChange} />
                                    {error && datatypeLimits && datatypeLimits.minimumValue && datatypeLimits.maximumValue &&
                                        <FormHelperText>
                                            {"The supported range is from " + datatypeLimits.minimumValue + " up to " + datatypeLimits.maximumValue + "."}
                                        </FormHelperText>
                                    }
                                    <TitledPanel dataTest="datatype-selection" height="full" margin title="Datatype">
                                        <MenuSelector
                                            dataTest="datatype-selector"
                                            autoFocus={false}
                                            handleChange={this.handleDatatypeChange}
                                            hintWord="datatype"
                                            selected={DATATYPES.STRING}
                                            options={[...(projectDatatype ?
                                                projectDatatype.map(dt => ({
                                                    label: "Custom (" + dt + ")",
                                                    value: dt
                                                }))
                                                :
                                                []
                                            ), ...menuOptions]} />
                                    </TitledPanel>
                                </form>
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <CancelButton onClick={this.exitAddLiteral} />
                                <PrimaryButton disabled={literal.trim().length === 0 || error || existing} onClick={this.addLiteral}>Add</PrimaryButton>
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>
                )}
            </ProjectConsumer>
        );
    }
}

TypedLiteralDialog.defaultProps = {
    excludeDatatypes: []
};

TypedLiteralDialog.propTypes = {
    dataTest: PropTypes.string,
    name: PropTypes.string.isRequired,
    existingValues: PropTypes.array,
    excludeDatatypes: PropTypes.array,
    placedholer: PropTypes.string,
    title: PropTypes.string.isRequired,
    textRef: PropTypes.string,
    onAddLiteral: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(TypedLiteralDialog);
